import React, { useEffect, useState, useCallback } from "react";
import {
  Box, useTheme, IconButton, Dialog, Slide, DialogTitle,
  Tooltip, TextField, InputAdornment, Paper, Typography,
  Chip, Stack, Grid, alpha, Button,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import ModeEditOutlineIcon from "@mui/icons-material/ModeEditOutline";
import SearchIcon from "@mui/icons-material/Search";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PlaceIcon from "@mui/icons-material/Place";
import AddBoxIcon from "@mui/icons-material/AddBox";
import { GetAllStationsAdmin } from "api/stations";
import StationForm from "./StationForm";

const Transition = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

const TYPE_CONFIG = {
  sort:    { label: "Sort",    color: "info" },
  transit: { label: "Transit", color: "warning" },
  final:   { label: "Final",   color: "success" },
  other:   { label: "Other",   color: "default" },
};

const getStationType = (row) => {
  if (row.isSortStation) return "sort";
  if (row.isTransitStation) return "transit";
  if (row.finalStation) return "final";
  return "other";
};

const StationCard = ({ station, onEdit, theme }) => {
  const type = getStationType(station);
  const { label, color } = TYPE_CONFIG[type];

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2, borderRadius: 2,
        borderColor: station.validflag ? theme.palette.divider : theme.palette.error.light,
        backgroundColor: station.validflag
          ? theme.palette.background.paper
          : alpha(theme.palette.error.main, 0.04),
        transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: 3 },
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <Box flex={1} minWidth={0}>
          {/* Header row */}
          <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" mb={0.5}>
            <Chip label={station.id} size="small" variant="outlined" sx={{ fontWeight: 700, fontSize: "0.72rem", height: 20 }} />
            <Chip label={label} size="small" color={color} sx={{ height: 20, fontSize: "0.72rem" }} />
            {!station.validflag && <Chip label="Inactive" size="small" color="error" variant="outlined" sx={{ height: 20, fontSize: "0.72rem" }} />}
          </Stack>

          {/* Station names */}
          <Typography variant="body2" fontWeight={600} noWrap>{station.name}</Typography>
          <Typography variant="caption" color="text.secondary" noWrap>{station.nameLA}</Typography>

          {/* Info row */}
          <Stack direction="row" spacing={1.5} mt={0.75} flexWrap="wrap">
            {station.parcelStatus && (
              <Typography variant="caption" color="text.secondary">
                Status: <strong>{station.parcelStatus}</strong>
              </Typography>
            )}
            {station.isFanshine && (
              <Chip label="Fanshine" size="small" color="warning" variant="outlined" sx={{ height: 18, fontSize: "0.65rem" }} />
            )}
            {station.isPhotoRequired && (
              <Stack direction="row" alignItems="center" spacing={0.25}>
                <PhotoCameraIcon sx={{ fontSize: 13, color: "text.secondary" }} />
                <Typography variant="caption" color="text.secondary">Photo</Typography>
              </Stack>
            )}
            {station.isCourierLabelRequired && (
              <Stack direction="row" alignItems="center" spacing={0.25}>
                <LocalShippingIcon sx={{ fontSize: 13, color: "text.secondary" }} />
                <Typography variant="caption" color="text.secondary">Courier Label</Typography>
              </Stack>
            )}
            {station.allowPackageCreation && (
              <Stack direction="row" alignItems="center" spacing={0.25}>
                <AddBoxIcon sx={{ fontSize: 13, color: "secondary.main" }} />
                <Typography variant="caption" color="secondary.main">Can Create Packages</Typography>
              </Stack>
            )}
            {station.googleMap && (
              <Stack direction="row" alignItems="center" spacing={0.25}>
                <PlaceIcon sx={{ fontSize: 13, color: "info.main" }} />
                <Typography variant="caption" color="info.main">Map</Typography>
              </Stack>
            )}
          </Stack>
        </Box>

        {/* Active indicator + Edit */}
        <Stack alignItems="center" spacing={0.5} pt={0.25}>
          {station.validflag
            ? <CheckCircleIcon color="success" fontSize="small" />
            : <CancelIcon color="disabled" fontSize="small" />
          }
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => onEdit(station)} sx={{ "&:hover": { color: theme.palette.warning.main } }}>
              <ModeEditOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Paper>
  );
};

const StationList = () => {
  const theme = useTheme();
  const [rows, setRows] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [editData, setEditData] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const loadStations = useCallback(async () => {
    const res = await GetAllStationsAdmin();
    if (res.success) { setRows(res.data); setFiltered(res.data); }
  }, []);

  useEffect(() => { loadStations(); }, [loadStations]);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(q
      ? rows.filter((r) => r.id?.toLowerCase().includes(q) || r.name?.toLowerCase().includes(q) || r.nameLA?.toLowerCase().includes(q))
      : rows
    );
  }, [search, rows]);

  const handleEdit = (row) => { setEditData(row); setDialogOpen(true); };
  const handleSaved = () => { setDialogOpen(false); loadStations(); };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <TextField
          size="small" sx={{ maxWidth: 320 }} placeholder="Search by ID or name..."
          value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>{filtered.length} station{filtered.length !== 1 ? "s" : ""}</Typography>
        <Button variant="contained" color="secondary" size="small" startIcon={<AddIcon />}
          onClick={() => { setEditData(null); setDialogOpen(true); }}>
          New Station
        </Button>
      </Stack>

      <Box sx={{ maxHeight: "68vh", overflowY: "auto", pr: 0.5 }}>
        <Grid container spacing={1.5}>
          {filtered.map((station) => (
            <Grid item xs={12} sm={6} md={4} key={station._id}>
              <StationCard station={station} onEdit={handleEdit} theme={theme} />
            </Grid>
          ))}
        </Grid>
        {filtered.length === 0 && (
          <Box textAlign="center" py={6} color="text.secondary">
            <PlaceIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
            <Typography>No stations found.</Typography>
          </Box>
        )}
      </Box>

      <Dialog open={dialogOpen} TransitionComponent={Transition} maxWidth="md" fullWidth onClose={() => setDialogOpen(false)}
        PaperProps={{ style: { backgroundColor: theme.palette.mode === "dark" ? theme.palette.primary[700] : theme.palette.grey[100] } }}
      >
        <DialogTitle>
          Edit Station
          <IconButton aria-label="close" onClick={() => setDialogOpen(false)} sx={{ position: "absolute", right: 8, top: 8 }}>
            <CloseOutlinedIcon />
          </IconButton>
        </DialogTitle>
        <Box px={3} pb={3}>
          <StationForm editData={editData} onSaved={handleSaved} onCancel={() => setDialogOpen(false)} />
        </Box>
      </Dialog>
    </Box>
  );
};

export default StationList;
